import { useNavigate } from "react-router-dom";
import { BadgeCheck, Scale, Factory, ArrowRight, FileCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { AnimatedSection } from "@/pages/home/shared";

const FEATURES = [
  { key: "f1", icon: BadgeCheck, stat: "99%+", statLabel: "purity" },
  { key: "f2", icon: Scale, stat: "±", statLabel: "exact fill" },
  { key: "f3", icon: Factory, stat: "US", statLabel: "sourced" },
];

export const AnalyticalTransparency = () => {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <section data-testid="analytical-transparency" className="relative py-20 md:py-28 overflow-hidden bg-[#0B0D11]">
      <div className="absolute inset-0 bg-gradient-to-b from-[#06070A] via-transparent to-[#06070A] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <AnimatedSection className="text-center mb-14">
          <span className="text-sm font-medium uppercase tracking-[0.2em] mb-4 block bg-gradient-to-r from-slate-300 to-slate-500 bg-clip-text text-transparent">
            {t("at_badge")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            {t("at_title_1")} <span className="metallic-text">{t("at_title_2")}</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">{t("at_subtitle")}</p>
        </AnimatedSection>

        <div className="grid gap-6 md:grid-cols-3">
          {FEATURES.map(({ key, icon: Icon }) => (
            <AnimatedSection key={key} className="h-full">
              <div
                data-testid={`at-feature-${key}`}
                className="coa-card-sweep group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-500/20 bg-[#101319] p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-slate-300/35 hover:shadow-[0_12px_40px_-12px_rgba(148,163,184,0.15)]"
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300/40 to-transparent" />
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-slate-500/25 bg-slate-400/[0.06]">
                  <Icon className="w-6 h-6 text-slate-300" />
                </span>
                <h3 className="text-lg font-bold text-white tracking-wide mb-2.5">{t(`at_${key}_title`)}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t(`at_${key}_desc`)}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection className="mt-12 text-center">
          <button
            type="button"
            onClick={() => navigate("/verify")}
            data-testid="coa-view-more-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#00AEEF] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1AA8FF] shadow-[0_8px_28px_-8px_rgba(0,174,239,0.45)]"
          >
            <FileCheck className="w-4 h-4" />
            {t("at_view_more")}
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-slate-500 mt-3">{t("at_view_more_sub")}</p>
        </AnimatedSection>
      </div>
    </section>
  );
};
